import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import { useDispatch, useSelector } from 'react-redux'
import Table from 'modules/Statistics/subcomponents/Layout/Table'
import PropTypes from 'prop-types'
import { notificationShow } from 'modules/Notifications/actions'
import queryString from 'query-string'
import prepareFilterParams from 'utils/prepareFilterParams'
import styles from 'modules/Statistics/subcomponents/Layout/Table/statistics-table.module.scss'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { Button, Grid, Icon } from 'gu-ui-kit'
import downloadFile from 'utils/downloadFile'
import { useTranslation } from 'react-i18next'
import MultipleSelect from 'components/MultipleSelect'
import { URL_FILE_XLS } from 'api/statistics/v2'
import { useTableConfig } from 'modules/Statistics/Main/subcomponents/TableComponent/hooks/useTableConfig'
import Select from 'components/Select'
import useEnableHiding from 'modules/Statistics/hooks/useEnableHiding'
import clsx from 'clsx'

const TableComponent = ({ filters, isDemoMode, chartVisible, setChartVisible }) => {
  const { table } = useSelector(({ statistics }) => statistics)
  const { mds, sm } = useBreakpoint()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [sorting, setSorting] = useState()
  const [tableGroup, setTableGroup] = useLocalStorage('statisticsTableGroup', {
    tableGroupBy: 'accounts',
  })
  const [groupBy, setGroupBy] = useState(tableGroup.tableGroupBy || 'account')
  const [statisticsMainTableConfig, setStatisticsMainTableConfig] = useLocalStorage(
    `statisticsMainTableConfig.${groupBy}`,
    { columnVisibility: {} },
  )
  const [columnVisibility, setColumnVisibility] = useState(
    statisticsMainTableConfig.columnVisibility,
  )

  const tableConfig = useTableConfig({
    groupBy,
    filters,
    summary: table.summary,
    sorting,
    setSorting,
    columnVisibility,
    setColumnVisibility,
    className: clsx('margin-bottom-8', styles['table'], styles['sticky'], {
      [styles[`table__${groupBy}`]]: groupBy,
    }),
  })

  const enableHiding = useEnableHiding(tableConfig)

  const setDefaultVisibility = useCallback(() => {
    setColumnVisibility(() =>
      enableHiding.reduce((acc, item) => {
        acc[item.value] = true
        return acc
      }, {}),
    )
  }, [setColumnVisibility, enableHiding])

  useEffect(() => {
    setStatisticsMainTableConfig({ columnVisibility })
  }, [columnVisibility])

  useEffect(() => {
    setTableGroup({ tableGroupBy: groupBy })
    setDefaultVisibility()
  }, [groupBy])

  useEffect(() => {
    setSorting(() => [{ id: tableConfig.columns[0].id, desc: true }])
    setDefaultVisibility()
  }, [])

  const fileUrl = useCallback(
    (url) => {
      const { id = null, desc = false } = sorting?.[0] ?? {}

      const sortingProp = id ? { sort: id, direction: desc ? 'desc' : 'asc' } : {}

      const search = queryString.stringify(
        prepareFilterParams({
          ...filters,
          columns: Object.keys(columnVisibility)
            .filter(({ id }) => id !== 'account.id')
            .map(({ id }) => (id === 'cost' && filters?.include_vat ? 'cost_with_vat' : id)),
          offset: 0,
          ...sortingProp,
        }),
        {
          arrayFormat: 'bracket',
        },
      )

      return url + '?' + search
    },
    [filters, columnVisibility, sorting],
  )

  const selectedColumns = useMemo(
    () =>
      Object.keys(columnVisibility).reduce((acc, item) => {
        if (columnVisibility[item]) {
          acc.push(item)
        }
        return acc
      }, []),
    [columnVisibility],
  )

  const handleDownloadError = useCallback(
    (error) =>
      dispatch(
        notificationShow({
          type: 'warning',
          message: error,
          delay: 20000,
        }),
      ),
    [dispatch],
  )

  return (
    <>
      <Grid
        gap={16}
        alignItems={'center'}
        className={styles['table_switcher-panel']}
        wrap={sm}
        justifyContent={'between'}
      >
        <Select
          buttonProps={{
            variant: 'secondary',
            size: sm ? 's' : 'm',
            grey: true,
            fullWidth: sm,
            align: sm ? 'center' : 'left',
          }}
          {...{
            key: 'table-switcher-table-group-switcher',
            name: 'table-group-switcher',
            value: {
              value: groupBy,
              label: `${t('statistics::table_switcher.group_by')}: ${t(
                'statistics::table_switcher.by',
                { context: groupBy },
              )}`,
            },
            values: [
              {
                value: 'accounts',
                label: t('statistics::table_switcher.by', {
                  context: 'accounts',
                }),
              },
              {
                value: 'networks',
                label: t('statistics::table_switcher.by', {
                  context: 'networks',
                }),
              },
            ],
            onChange: setGroupBy,
          }}
        />

        <Grid
          fluid={sm}
          wrap={false}
          gap={sm ? 8 : 16}
          alignItems={'center'}
          justifyContent={sm ? 'space-between' : 'flex-end'}
        >
          <MultipleSelect
            buttonProps={{
              size: sm ? 's' : 'm',
            }}
            name={'columns'}
            type={'multiple'}
            placeholder={t('statistics::table_switcher.choose_columns_placeholder')}
            listTitle={t('statistics::table_switcher.choose_columns')}
            value={selectedColumns}
            values={enableHiding}
            onChange={(newSelection) =>
              setColumnVisibility(
                enableHiding.reduce((acc, item) => {
                  acc[item.value] = newSelection.includes(item.value)
                  return acc
                }, {}),
              )
            }
            onReset={setDefaultVisibility}
            apply={t('common::button_labels.apply')}
            dropSelection={t('common::button_labels.drop_selection')}
          />

          {[
            {
              type: 'xls',
              fileUrl: fileUrl(URL_FILE_XLS({ groupBy })),
            },
          ].map(({ type, fileUrl }) => (
            <Button
              onClick={() => downloadFile(fileUrl, 'statistics', type).catch(handleDownloadError)}
              key={type}
              variant={'ghost'}
              grey
              noLabel={mds && !sm}
              size={sm ? 's' : 'm'}
              startIcon={<Icon name={'file-' + type} />}
            >
              {t('statistics::buttons.download.label')}
            </Button>
          ))}

          {chartVisible !== undefined && (
            <Button
              onClick={() => setChartVisible(!chartVisible)}
              className={styles['hide-chart-button']}
              variant={'ghost'}
              grey
              size={sm ? 's' : 'm'}
              noLabel={mds}
              startIcon={
                <Icon
                  name={chartVisible ? 'eye-hidden' : 'eye-visible'}
                  color={'inherit'}
                />
              }
            >
              {t('statistics::filters.chart_visiblity', {
                context: chartVisible ? 'hide' : 'show',
              })}
            </Button>
          )}
        </Grid>
      </Grid>
      <Table
        table={table}
        isDemoMode={isDemoMode}
        tableConfig={tableConfig}
        groupBy={groupBy}
        filters={filters}
        dropTableData={() =>
          dispatch({
            type: 'STATISTICS/DROP_TABLE_DATA',
          })
        }
        onTableParamsUpdate={(params) =>
          dispatch({
            type: 'STATISTICS/FETCH_TABLE',
            payload: params,
            isDemoMode,
          })
        }
      />
    </>
  )
}

TableComponent.propTypes = {
  filters: PropTypes.object.isRequired,
  isDemoMode: PropTypes.bool,
  chartVisible: PropTypes.bool,
  setChartVisible: PropTypes.func,
}

export default TableComponent
